import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { Plus, Trash, XIcon } from 'lucide-react-native';
import { useEffect, useState } from 'react';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { ScrollView, Text } from 'react-native';
import { twJoin } from 'tailwind-merge';

import { getCancelHeaderProps, headerItem } from '@/components/HeaderActions';
import { Header } from '@/components/header';
import { Button, ButtonText } from '@/components/ui/button';
import { Heading } from '@/components/ui/heading';
import { HStack } from '@/components/ui/hstack';
import { Icon } from '@/components/ui/icon';
import { Input, InputField } from '@/components/ui/input';
import {
  Modal,
  ModalBackdrop,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from '@/components/ui/modal';
import { Spinner } from '@/components/ui/spinner';
import { VStack } from '@/components/ui/vstack';
import { useConfirmDiscard } from '@/components/useConfirmDiscard';
import { ANALYTICS_EVENTS } from '@/constants/analytics';
import { ROUTES } from '@/constants/routes';
import { useGoBack } from '@/hooks/useGoBack';
import { useToaster } from '@/hooks/useToaster';
import { getUserV2QueryOptions } from '@/services/getUserV2';
import { userIdAtom } from '@/services/globalState';
import { updateUser } from '@/services/updateUser';
import { segmentClient } from '@/util/analytics';

interface DisclosureFormValues {
  disclosures: {
    name: string;
    description: string;
  }[];
}

/**
 * EditDisclosuresScreen - Profile disclosure management component
 *
 * Allows users to add, edit, and delete disclosure entries for their profile.
 * Features include:
 * - Dynamic form with add/remove disclosure entries
 * - Real-time validation and dirty state tracking
 * - Optimistic updates with cache invalidation
 * - Delete confirmation modal with error handling
 * - Cross-platform header rendering (web/mobile)
 * - Analytics tracking for profile edits
 */
function EditDisclosuresScreen() {
  const userId = useAtomValue(userIdAtom);

  const goBack = useGoBack(ROUTES.ACCOUNT_PROFILE);

  const queryClient = useQueryClient();

  const toaster = useToaster();

  const { data: userData, isLoading } = useQuery(getUserV2QueryOptions(userId));

  const { mutateAsync: updateUserMutation, isPending: isSaving } = useMutation({
    mutationFn: updateUser,
  });

  const form = useForm<DisclosureFormValues>({
    defaultValues: {
      disclosures: [],
    },
    mode: 'onChange',
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'disclosures',
  });

  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const [pendingDeleteIndex, setPendingDeleteIndex] = useState<number | null>(null);

  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (userData) {
      form.reset({
        disclosures: (userData.disclosures || []).map((d) => ({
          name: d.name || '',
          description: d.description || '',
        })),
      });
    }
  }, [userData, form]);

  const handleBack = () => {
    goBack();
  };

  const { confirmCancel, discardModal } = useConfirmDiscard({
    isDirty: form.formState.isDirty,
    onDiscard: handleBack,
  });

  const onSubmit = async (values: DisclosureFormValues) => {
    await updateUserMutation({ disclosures: values.disclosures });

    const updated = { ...(userData || {}), disclosures: values.disclosures };

    queryClient.setQueryData(['usersV2', userId], updated);

    queryClient.invalidateQueries({ queryKey: ['usersV2'] });

    segmentClient.track(ANALYTICS_EVENTS.PROFILE_EDIT_SAVED, { editType: 'disclosures' });

    toaster.showSuccess({ title: 'Disclosures Updated' });

    handleBack();
  };

  const renderHeader = () => {
    return (
      <>
        <Header
          {...getCancelHeaderProps(confirmCancel)}
          contentScrollsUnderHeader
          title="Edit Disclosures"
          rightItems={[
            headerItem.save({
              onPress: form.handleSubmit(onSubmit),
              isDisabled: !form.formState.isDirty,
              isSaving,
            }),
          ]}
        />

        {discardModal}
      </>
    );
  };

  if (isLoading) {
    return (
      <VStack className="flex-1 items-center justify-center">
        <Spinner size="large" />
      </VStack>
    );
  }

  return (
    <VStack className={isWeb ? 'mx-auto w-full max-w-screen-md flex-1' : 'flex-1 bg-background-0'}>
      {renderHeader()}
      <ScrollView
        className="flex-1"
        contentInsetAdjustmentBehavior="automatic"
        contentContainerStyle={{ paddingBottom: 40 }}>
        <VStack space="md" className="px-6 pt-6">
          {fields.map((field, idx) => (
            <HStack key={field.id} className="items-center gap-2">
              <Controller
                control={form.control}
                name={`disclosures.${idx}.name` as const}
                render={({ field: f }) => (
                  <Input className="flex-1">
                    <InputField
                      placeholder="Name"
                      value={f.value}
                      onChangeText={f.onChange}
                      onBlur={f.onBlur}
                    />
                  </Input>
                )}
              />
              <Controller
                control={form.control}
                name={`disclosures.${idx}.description` as const}
                render={({ field: f }) => (
                  <Input className="flex-1">
                    <InputField
                      placeholder="Description"
                      value={f.value}
                      onChangeText={f.onChange}
                      onBlur={f.onBlur}
                    />
                  </Input>
                )}
              />
              <Button
                variant="link"
                size="md"
                onPress={() => {
                  setPendingDeleteIndex(idx);

                  setShowDeleteModal(true);
                }}
                hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}>
                <Icon as={Trash} size="md" className="m-0 p-0 text-primary-600" />
              </Button>
            </HStack>
          ))}
          <Button
            variant="outline"
            size="sm"
            className="mt-4 self-start"
            onPress={() => append({ name: '', description: '' })}>
            <Icon as={Plus} size="sm" className="mr-2 text-typography-500" />
            <ButtonText>Add Disclosure</ButtonText>
          </Button>
        </VStack>
      </ScrollView>
      <Modal isOpen={showDeleteModal} onClose={() => setShowDeleteModal(false)} size="md">
        <ModalBackdrop />
        <ModalContent>
          <ModalHeader>
            <Heading size="h2-xl">Delete entry</Heading>
            <ModalCloseButton onPress={() => setShowDeleteModal(false)}>
              <Icon as={XIcon} />
            </ModalCloseButton>
          </ModalHeader>
          <ModalBody>
            <Text>Do you want to proceed?</Text>
          </ModalBody>
          <ModalFooter className="w-full flex-col justify-start gap-4">
            <Button
              disabled={deleting}
              className={twJoin('w-full', deleting && 'opacity-50')}
              onPress={async () => {
                if (pendingDeleteIndex == null) {
                  return;
                }

                setDeleting(true);

                try {
                  remove(pendingDeleteIndex);

                  const updated = fields
                    .filter((_, i) => i !== pendingDeleteIndex)
                    .map((f) => ({ name: f.name, description: f.description }));

                  await updateUserMutation({ disclosures: updated });

                  queryClient.invalidateQueries({ queryKey: ['usersV2'] });

                  setShowDeleteModal(false);

                  setPendingDeleteIndex(null);

                  toaster.showSuccess({ title: 'Disclosure entry deleted' });
                } catch (error) {
                  toaster.showError({ title: 'Failed to delete entry' });
                } finally {
                  setDeleting(false);
                }
              }}>
              {deleting ? <Spinner size="small" /> : <ButtonText>Delete entry</ButtonText>}
            </Button>
            <Button
              className="w-full"
              variant="outline"
              action="secondary"
              onPress={() => setShowDeleteModal(false)}>
              <ButtonText>Continue editing</ButtonText>
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </VStack>
  );
}

export default EditDisclosuresScreen;
